import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Loader2, RefreshCw } from 'lucide-react';

interface LoginScreenProps {
  onSignInGoogle: () => Promise<{ success: boolean; error?: string }>;
  hideButtons?: boolean;
  isLoading?: boolean;
  isReadyToPlay?: boolean;
  onContinue?: () => void;
}

export default function LoginScreen({ 
  onSignInGoogle, 
  hideButtons = false,
  isLoading = false,
  isReadyToPlay = false,
  onContinue
}: LoginScreenProps) {
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const messages = [
    "Connecting to Play Games...",
    "Initializing Systems...",
    "Loading Database...",
    "Fetching User Profile...",
    "Synchronizing Progress...",
    "Warming up Cores...",
    "Connecting to Firestore..."
  ];
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    if (isLoading || isLoggingIn) {
      const interval = setInterval(() => {
        setMsgIndex(prev => (prev + 1) % messages.length);
      }, 800);
      return () => clearInterval(interval);
    }
  }, [isLoading, isLoggingIn]);

  const [hasAttempted, setHasAttempted] = useState(false);

  // Auto-trigger sign-in on boot
  useEffect(() => {
    if (!isLoading && !hideButtons && !isLoggingIn && !loginError && !hasAttempted) {
      if (localStorage.getItem('block_auto_signin') === 'true') {
        console.log("Auto sign-in blocked due to explicit logout/account-deletion");
        return;
      }
      setHasAttempted(true);
      handleAutoSignIn();
    }
  }, [isLoading, hideButtons, loginError, hasAttempted]);

  const handleAutoSignIn = async () => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      localStorage.removeItem('block_auto_signin');
      const res = await onSignInGoogle();
      if (!res.success) {
        setLoginError(res.error || "Failed to connect to Play Games Services.");
      }
    } catch (err: any) {
      setLoginError(err.message || "An unexpected error occurred during sign-in.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div 
      className="relative h-[100dvh] w-full overflow-hidden cursor-pointer"
      onClick={() => {
        if (isReadyToPlay) {
          onContinue();
        }
      }}
    >
      {/* Background Image Screen */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("/CS_Page.png")' }}
      >
        {/* Ambient overlay to blend with dark elements/enhance readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/60" />
      </div>

      {/* Content Overlay */}
      <div className="relative z-20 h-full flex flex-col items-center justify-between py-24 px-8">
        {/* Top Logo / Title */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 80 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col items-center"
        >
          <img 
            src="/numq logo.png" 
            alt="numQ" 
            className="w-48 h-auto drop-shadow-2xl"
          />
        </motion.div>

        {/* Loading / Login Content */}
        <div className="w-full max-w-xs space-y-6 flex flex-col items-center">
          {isReadyToPlay ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-full flex flex-col items-center pointer-events-none"
            >
              <span className="text-[#fbbf24] font-black text-lg uppercase tracking-[0.25em] drop-shadow-[0_0_15px_rgba(251,191,36,0.65)] select-none">
                TOUCH TO START
              </span>
            </motion.div>
          ) : isLoading || isLoggingIn ? (
            <div className="w-full space-y-4 flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-white animate-spin drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
              <div className="w-full h-3 bg-slate-900/20 backdrop-blur-md rounded-full overflow-hidden border border-white/10 p-[2px]">
                <motion.div 
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 3, ease: "easeInOut" }}
                  className="h-full bg-white rounded-full shadow-[0_0_15px_rgba(255,255,255,0.5)]"
                />
              </div>
              <p className="text-white/60 text-[10px] text-center font-black uppercase tracking-[0.3em] animate-pulse">
                {isLoading ? messages[msgIndex] : "Connecting to Play Games Services..."}
              </p>
            </div>
          ) : loginError ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full space-y-4 flex flex-col items-center"
            >
              <div className="bg-rose-500/20 backdrop-blur-md border border-rose-500/30 rounded-2xl p-4 text-center w-full">
                <p className="text-rose-200 text-xs font-black uppercase tracking-wider mb-2">Connection Required</p>
                <p className="text-white/80 text-[10px] leading-relaxed">{loginError}</p>
              </div>

              <button
                onClick={handleAutoSignIn}
                className="w-full py-4 px-6 bg-white text-slate-900 rounded-2xl font-black flex items-center justify-center gap-3 shadow-xl hover:scale-105 active:scale-95 transition-all"
              >
                <RefreshCw className="w-5 h-5 animate-spin-hover" />
                RETRY PLAY GAMES SIGN-IN
              </button>

              <p className="text-white/40 text-[9px] text-center font-medium uppercase tracking-widest mt-2">
                A valid Google Play Games profile is required to play.
              </p>
            </motion.div>
          ) : localStorage.getItem('block_auto_signin') === 'true' ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full space-y-4 flex flex-col items-center"
            >
              <button
                onClick={handleAutoSignIn}
                className="w-full py-5 px-6 bg-white text-slate-900 rounded-[24px] font-black flex items-center justify-center gap-3 shadow-xl hover:scale-105 active:scale-95 transition-all text-sm uppercase tracking-wider"
              >
                SIGN IN WITH PLAY GAMES
              </button>
            </motion.div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
